import { createContext, useEffect, useReducer } from 'react'
import PropTypes from 'prop-types'
import api from '../api'
import { useRouter } from 'next/router'
import { authApi } from '../api/auth-api'
import { useMounted } from '../hooks/use-mounted'
import { useNotification } from '../hooks/use-notification'
import { gtm } from '../lib/gtm'
import { clearItemsFromLocalStorage } from 'slices/data-table'
import { api as legacyApiSlice } from '../services/api'
import { laravel_api } from '../services/laravel_api'
import { useDispatch as useReduxDispatch } from '../store'

/**
 * Partial shape of the authenticated user (legacy UserPayload). Only the fields
 * currently read from the auth context across the app are typed here; extend as
 * the payload is properly modelled.
 */
interface AuthUser {
    usr_logo_url?: string
    usr_is_approved?: number
    account_type?: string
    campaign_reporting_overwrite_is_enabled?: boolean
    usr_first_name?: string
    usr_last_name?: string
    usr_avatar_url?: string
}

const initialState = {
    isAuthenticated: false,
    isInitialized: false,
    isLoggedOut: true,
    accountSwitchModeIsOpen: false,
    organizationSwitchModeIsOpen: false,

    configCommonCountriesCampaignTypeOptions: null,
    configCommonCountriesCampaignTypeDefaults: null,

    countries_support: {
        audience_target: ['US'],
        geofence: ['US']
    },

    acl: {
        isViewModeOnly: false,
        hasStandardAccess: false,
        hasAccountManagerAccess: false,
        hasAgencyManagerAccess: false,
        hasReferralPartnerAccess: false,
        hasBookkeeperAccess: false,
        hasAdminAccess: false,
        hasSuperAdminAccess: false,
        allowed_networks: []
    },
    user: {},
    activeNetwork: null,
    userDefaultNetwork: null,
    userDefaultNetworkAcl: {
        accountType: 'na',
        isViewModeOnly: false,
        hasStandardAccess: false,
        hasAccountManagerAccess: false,
        hasAgencyManagerAccess: false,
        hasReferralPartnerAccess: false,
        hasBookkeeperAccess: false,
        hasAdminAccess: false,
        hasSuperAdminAccess: false,
        allowed_networks: []
    },
    accountAccessLevel: 0,
    segmentsCustom: [],
    userNetworks: [],
    userRecentAccounts: [],
    userLogsAuth: [],
    organizationAccounts: [],
    errorMessage: null,
    uploadingAvatar: false,
    updatePasswordRequest: false
}

const handlers = {
    OPEN_ACCOUNT_SWITCH: (state) => {
        return {
            ...state,
            accountSwitchModeIsOpen: true
        }
    },
    CLOSE_ACCOUNT_SWITCH: (state) => {
        return {
            ...state,
            accountSwitchModeIsOpen: false
        }
    },

    OPEN_ORGANIZATION_SWITCH: (state) => {
        return {
            ...state,
            organizationSwitchModeIsOpen: true
        }
    },
    CLOSE_ORGANIZATION_SWITCH: (state) => {
        return {
            ...state,
            organizationSwitchModeIsOpen: false
        }
    },

    CHANGE_DEFAULT_NETWORK: (state, action) => {
        return {
            ...state,
            userDefaultNetwork: action.payload.network_id
        }
    },
    UPDATE_PASSWORD_REQUEST: (state) => {
        return {
            ...state,
            updatePasswordRequest: true
        }
    },
    UPDATE_PASSWORD_FINISHED: (state, action) => {
        const { errorMessage } = action.payload

        return {
            ...state,
            errorMessage,
            updatePasswordRequest: true
        }
    },
    INITIALIZE: (state, action) => {
        const {
            acl,
            isAuthenticated,
            user,
            activeNetwork,
            userDefaultNetwork,
            userDefaultNetworkAcl,
            segmentsCustom,
            userNetworks,
            userRecentAccounts,
            userLogsAuth,
            errorMessage,
            organizationAccounts,

            countries_support,
            configCommonCountriesCampaignTypeOptions,
            configCommonCountriesCampaignTypeDefaults
        } = action.payload

        if (errorMessage) {
            return {
                ...state,
                isInitialized: false,
                acl: {
                    ...initialState.acl
                },
                userDefaultNetworkAcl: {
                    ...initialState.acl
                },
                errorMessage
            }
        }

        return {
            ...state,
            isAuthenticated,
            acl,
            isInitialized: true,
            user,
            activeNetwork,
            userDefaultNetwork,
            userDefaultNetworkAcl,
            countries_support,
            configCommonCountriesCampaignTypeOptions,
            configCommonCountriesCampaignTypeDefaults,
            userNetworks,
            userRecentAccounts,
            organizationAccounts,
            segmentsCustom,
            userLogsAuth,
            errorMessage: null
        }
    },
    INITIALIZE_ERROR: (state, action) => {
        const { errorMessage } = action.payload

        if (errorMessage) {
            return {
                ...state,
                errorMessage,
                acl: {
                    ...initialState.acl
                },
                userDefaultNetworkAcl: {
                    ...initialState.acl
                },
                isInitialized: true
            }
        }

        return {
            ...state,
            isAuthenticated: false,
            isInitialized: true,
            user: {},
            activeNetwork: null,
            userDefaultNetwork: null,
            acl: {
                ...initialState.acl
            },
            userDefaultNetworkAcl: {
                ...initialState.acl
            },
            userNetworks: [],
            userLogsAuth: [],
            errorMessage: null
        }
    },

    LOGIN: (state, action) => {
        const {
            user,
            activeNetwork,
            userDefaultNetwork,
            userDefaultNetworkAcl,
            userNetworks,
            userRecentAccounts,
            segmentsCustom,
            userLogsAuth,
            acl,
            errorMessage,
            organizationAccounts
        } = action.payload

        if (errorMessage) {
            return {
                ...state,
                errorMessage
            }
        }

        return {
            ...state,
            isAuthenticated: true,
            isLoggedOut: false,
            acl,
            user,
            activeNetwork,
            userDefaultNetwork,
            userDefaultNetworkAcl,
            userNetworks,
            userRecentAccounts,
            organizationAccounts,
            segmentsCustom,
            userLogsAuth,
            errorMessage: null
        }
    },
    UPDATE: (state, action) => {
        const {
            user,
            activeNetwork,
            userDefaultNetwork,
            userDefaultNetworkAcl,
            userNetworks,
            userRecentAccounts,
            segmentsCustom,
            userLogsAuth,
            errorMessage,
            organizationAccounts
        } = action.payload

        if (errorMessage) {
            return {
                ...state,
                errorMessage
            }
        }

        return {
            ...state,
            user,
            activeNetwork,
            userDefaultNetwork,
            userDefaultNetworkAcl,
            userNetworks,
            userRecentAccounts,
            organizationAccounts,
            segmentsCustom,
            userLogsAuth,
            errorMessage: null
        }
    },

    LOGOUT: (state) => ({
        ...state,
        isLoggedOut: true,
        isAuthenticated: false,
        user: {},
        activeNetwork: null,
        userDefaultNetwork: null,
        acl: {
            ...initialState.acl
        },
        userDefaultNetworkAcl: {
            ...initialState.acl
        },
        segmentsCustom: [],
        userNetworks: [],
        userRecentAccounts: [],
        organizationAccounts: [],
        userLogsAuth: []
    }),

    REGISTER: (state, action) => {
        const { errorMessage } = action.payload

        if (errorMessage) {
            return {
                ...state,
                errorMessage
            }
        }

        return {
            ...state,
            errorMessage: null
        }
    },
    UPLOAD_AVATAR_REQUEST: (state, action) => {
        const {} = action.payload

        return {
            ...state,
            uploadingAvatar: true
        }
    },
    UPLOAD_AVATAR_RESPONSE: (state, action) => {
        const {
            user,
            activeNetwork,
            userDefaultNetwork,
            userDefaultNetworkAcl,
            userNetworks,
            userRecentAccounts,
            segmentsCustom,
            userLogsAuth,
            errorMessage,
            organizationAccounts
        } = action.payload

        if (errorMessage) {
            return {
                ...state,
                uploadingAvatar: false,
                errorMessage
            }
        }

        return {
            ...state,
            uploadingAvatar: false,
            user,
            activeNetwork,
            userDefaultNetwork,
            userDefaultNetworkAcl,
            segmentsCustom,
            userNetworks,
            userRecentAccounts,
            organizationAccounts,
            userLogsAuth,
            errorMessage: null
        }
    }
}

const reducer = (state, action) =>
    handlers[action.type] ? handlers[action.type](state, action) : state

export const AuthContext = createContext({
    ...initialState,
    user: {} as AuthUser,
    platform: 'session',
    update: () => Promise.resolve(),
    login: () => Promise.resolve(),
    logout: () => Promise.resolve(),
    register: () => Promise.resolve(),
    recover: () => Promise.resolve(),
    recoverVerify: () => Promise.resolve(),
    uploadLogo: (_file) => Promise.resolve({ errorMessage: null }),
    removeLogo: () => Promise.resolve({ errorMessage: null })
})

export const AuthProvider = (props) => {
    const { children } = props
    const isMounted = useMounted()
    const router = useRouter()
    const notificationContext = useNotification()

    const [state, dispatch] = useReducer(reducer, initialState)
    const reduxDispatch = useReduxDispatch()

    useEffect(() => {
        const initialize = async () => {
            // Cookie/session auth: always ask the API who the current user is.
            // A valid session cookie yields the user payload; otherwise the API
            // responds unauthenticated and we fall back to a guest state (no error
            // toast — an unauthenticated visitor on boot is expected, not a failure).
            // Prime the CSRF cookie first — /api/auth/me is a POST and needs it.
            await api
                .sentRequest(
                    '/sanctum/csrf-cookie',
                    null,
                    false,
                    null,
                    null,
                    'GET'
                )
                .catch(() => null)
            const apiResponse = await authApi.me()
            const { errorMessage } = apiResponse || {}
            const {
                acl,
                user,
                activeNetwork,
                userDefaultNetwork,
                userDefaultNetworkAcl,
                userNetworks,
                userRecentAccounts,
                segmentsCustom,
                userLogsAuth,
                organizationAccounts,

                countries_support,
                configCommonCountriesCampaignTypeOptions,
                configCommonCountriesCampaignTypeDefaults
            } = apiResponse.body || {}

            if (!errorMessage && user) {
                dispatch({
                    type: 'INITIALIZE',
                    payload: {
                        isAuthenticated: true,
                        acl,
                        user,
                        activeNetwork,
                        userDefaultNetwork,
                        userDefaultNetworkAcl,
                        segmentsCustom,
                        userNetworks,
                        userRecentAccounts,
                        organizationAccounts,
                        userLogsAuth,

                        countries_support,
                        configCommonCountriesCampaignTypeOptions,
                        configCommonCountriesCampaignTypeDefaults
                    }
                })

                updateDataLayer(user)
            } else {
                dispatch({
                    type: 'INITIALIZE',
                    payload: {
                        isAuthenticated: false,

                        countries_support: {
                            audience_target: ['US'],
                            geofence: ['US']
                        },
                        configCommonCountriesCampaignTypeOptions: null,
                        configCommonCountriesCampaignTypeDefaults: null,

                        acl: {
                            ...initialState.acl
                        },
                        user: {},
                        activeNetwork: null,
                        userDefaultNetwork: null,
                        userDefaultNetworkAcl: {
                            ...initialState.acl
                        },
                        segmentsCustom: [],
                        userNetworks: [],
                        userRecentAccounts: [],
                        organizationAccounts: [],
                        userLogsAuth: []
                    }
                })
            }
        }

        initialize()
    }, [router.isReady, isMounted])

    const login = async (email, password, termsAccepted, termsVersion) => {
        // Sanctum SPA: prime the CSRF cookie before the stateful login POST.
        await api.sentRequest(
            '/sanctum/csrf-cookie',
            null,
            false,
            null,
            null,
            'GET'
        )

        const apiResponse = await authApi.login({
            email,
            password,
            termsAccepted,
            termsVersion
        })

        const body = apiResponse?.body || {}
        const can_login = body.can_login === true
        const accountAccessErrors =
            apiResponse?.accountAccessErrors || body.accountAccessErrors || []
        const errorMessage =
            apiResponse?.message || apiResponse?.errorMessage || null

        if (!can_login) {
            return {
                can_login: false,
                user: null,
                errorMessage,
                accountAccessErrors
            }
        }

        // Login calls session()->regenerate(), which rotates the CSRF token.
        // Refresh the XSRF-TOKEN cookie before the next request so /me isn't
        // rejected with the pre-login (stale) token.
        await api
            .sentRequest('/sanctum/csrf-cookie', null, false, null, null, 'GET')
            .catch(() => null)

        // Session established — hydrate the full user payload from /me.
        const meResponse = await me()
        const user = meResponse?.body?.user || null

        return {
            can_login: true,
            user,
            errorMessage: null,
            accountAccessErrors: []
        }
    }

    const update = async (
        first_name,
        last_name,
        phone_number,
        company_name,
        company_url,
        country_code
    ) => {
        const apiResponse = await authApi.update({
            first_name,
            last_name,
            phone_number,
            company_name,
            company_url,
            country_code
        })

        const { errorCode, errorMessage } = apiResponse || {}
        const {
            user,
            activeNetwork,
            userDefaultNetwork,
            userDefaultNetworkAcl,
            userNetworks,
            userRecentAccounts,
            segmentsCustom,
            userLogsAuth,
            organizationAccounts
        } = apiResponse.body || {}

        notificationContext.apiError(apiResponse, 'Updating User Profile')
        notificationContext.apiSuccess(
            apiResponse,
            'Updating User Profile',
            'Your account has been updated.'
        )

        updateDataLayer(user)

        dispatch({
            type: 'UPDATE',
            payload: {
                user,
                activeNetwork,
                userDefaultNetwork,
                userDefaultNetworkAcl,
                segmentsCustom,
                userNetworks,
                userRecentAccounts,
                organizationAccounts,
                userLogsAuth,
                errorMessage
            }
        })

        return apiResponse
    }

    const aboutMe = async () => {
        const apiResponse = await authApi.me()

        const { errorCode, errorMessage } = apiResponse || {}
        const {
            user,
            activeNetwork,
            userDefaultNetwork,
            userDefaultNetworkAcl,
            userNetworks,
            userRecentAccounts,
            segmentsCustom,
            userLogsAuth,
            organizationAccounts
        } = apiResponse.body || {}

        dispatch({
            type: 'UPDATE',
            payload: {
                user,
                activeNetwork,
                userDefaultNetwork,
                userDefaultNetworkAcl,
                segmentsCustom,
                userNetworks,
                userRecentAccounts,
                organizationAccounts,
                userLogsAuth,
                errorMessage
            }
        })

        return apiResponse
    }

    const me = async () => {
        const apiResponse = await authApi.me()

        const { errorCode, errorMessage } = apiResponse || {}
        const {
            acl,
            user,
            activeNetwork,
            userDefaultNetwork,
            userDefaultNetworkAcl,
            userNetworks,
            userRecentAccounts,
            segmentsCustom,
            userLogsAuth,
            organizationAccounts,
            countries_support,
            configCommonCountriesCampaignTypeOptions,
            configCommonCountriesCampaignTypeDefaults
        } = apiResponse.body || {}

        notificationContext.apiError(apiResponse, 'Loading User Profile')

        dispatch({
            type: 'INITIALIZE',
            payload: {
                isAuthenticated: !errorMessage && !!user,
                user,
                activeNetwork,
                userDefaultNetwork,
                userDefaultNetworkAcl,
                segmentsCustom,
                userNetworks,
                userRecentAccounts,
                organizationAccounts,
                userLogsAuth,
                acl,
                errorMessage,

                countries_support,
                configCommonCountriesCampaignTypeOptions,
                configCommonCountriesCampaignTypeDefaults
            }
        })

        if (errorMessage) {
            await logout()
        }

        updateDataLayer(user)

        return apiResponse
    }

    const changeMenuVisibility = async (usr_menu_visible) => {
        const apiResponse = await authApi.changeMenuVisibility(usr_menu_visible)

        const { errorCode, errorMessage } = apiResponse || {}
        const {
            acl,
            user,
            activeNetwork,
            userDefaultNetwork,
            userDefaultNetworkAcl,
            userNetworks,
            userRecentAccounts,
            segmentsCustom,
            userLogsAuth,
            organizationAccounts
        } = apiResponse.body || {}

        dispatch({
            type: 'UPDATE',
            payload: {
                user,
                activeNetwork,
                userDefaultNetwork,
                userDefaultNetworkAcl,
                segmentsCustom,
                userNetworks,
                userRecentAccounts,
                organizationAccounts,
                userLogsAuth,
                errorMessage
            }
        })

        return apiResponse
    }

    const tryToReLoginWithToken = async () => {
        const apiResponse = await authApi.me()

        const { errorCode, errorMessage } = apiResponse || {}
        const {
            acl,
            user,
            activeNetwork,
            userDefaultNetwork,
            userDefaultNetworkAcl,
            userNetworks,
            userRecentAccounts,
            segmentsCustom,
            userLogsAuth,
            organizationAccounts
        } = apiResponse.body || {}

        notificationContext.apiError(apiResponse, 'User Authorization')
        notificationContext.apiInfo(
            apiResponse,
            'You are logged in',
            'Welcome back! We hope you have a good day!'
        )

        dispatch({
            type: 'LOGIN',
            payload: {
                user,
                activeNetwork,
                userDefaultNetwork,
                userDefaultNetworkAcl,
                segmentsCustom,
                userNetworks,
                userRecentAccounts,
                organizationAccounts,
                userLogsAuth,
                acl,
                errorMessage
            }
        })

        if (errorMessage) {
            await logout()
        }

        updateDataLayer(user)

        return apiResponse
    }

    const uploadAvatar = async (avatar) => {
        dispatch({
            type: 'UPLOAD_AVATAR_REQUEST',
            payload: {}
        })

        const apiResponse = await authApi.uploadAvatar(avatar)

        const { errorCode, errorMessage } = apiResponse || {}
        const {
            user,
            activeNetwork,
            userDefaultNetwork,
            userDefaultNetworkAcl,
            userNetworks,
            userRecentAccounts,
            segmentsCustom,
            userLogsAuth,
            organizationAccounts
        } = apiResponse.body || {}

        dispatch({
            type: 'UPLOAD_AVATAR_RESPONSE',
            payload: {
                user,
                activeNetwork,
                userDefaultNetwork,
                userDefaultNetworkAcl,
                userNetworks,
                userRecentAccounts,
                segmentsCustom,
                userLogsAuth,
                errorMessage,
                organizationAccounts
            }
        })

        notificationContext.apiError(apiResponse, 'Uploading User Avatar')
        notificationContext.apiSuccess(
            apiResponse,
            'User Avatar Updated',
            'Your new avatar was uploaded successfully!'
        )

        return apiResponse
    }

    const uploadLogo = async (logo) => {
        dispatch({
            type: 'UPLOAD_AVATAR_REQUEST',
            payload: {}
        })

        const apiResponse = await authApi.uploadLogo(logo)

        const { errorCode, errorMessage } = apiResponse || {}
        const {
            user,
            activeNetwork,
            userDefaultNetwork,
            userDefaultNetworkAcl,
            userNetworks,
            userRecentAccounts,
            segmentsCustom,
            userLogsAuth,
            organizationAccounts
        } = apiResponse.body || {}

        dispatch({
            type: 'UPLOAD_AVATAR_RESPONSE',
            payload: {
                user,
                activeNetwork,
                userDefaultNetwork,
                userDefaultNetworkAcl,
                userNetworks,
                userRecentAccounts,
                segmentsCustom,
                userLogsAuth,
                errorMessage,
                organizationAccounts
            }
        })

        notificationContext.apiError(apiResponse, 'Uploading User Avatar')
        notificationContext.apiSuccess(
            apiResponse,
            'User Avatar Updated',
            'Your new avatar was uploaded successfully!'
        )

        return apiResponse
    }

    const removeLogo = async () => {
        dispatch({
            type: 'UPLOAD_AVATAR_REQUEST',
            payload: {}
        })

        const apiResponse = await authApi.removeLogo(state.user.user_id)

        const { errorMessage } = apiResponse || {}
        const {
            user,
            activeNetwork,
            userDefaultNetwork,
            userDefaultNetworkAcl,
            userNetworks,
            userRecentAccounts,
            segmentsCustom,
            userLogsAuth,
            organizationAccounts
        } = apiResponse.body || {}

        dispatch({
            type: 'UPLOAD_AVATAR_RESPONSE',
            payload: {
                user,
                activeNetwork,
                userDefaultNetwork,
                userDefaultNetworkAcl,
                userNetworks,
                userRecentAccounts,
                segmentsCustom,
                userLogsAuth,
                errorMessage,
                organizationAccounts
            }
        })

        notificationContext.apiError(apiResponse, 'Removing Logo')
        notificationContext.apiSuccess(
            apiResponse,
            'Logo Removed',
            'Your logo was removed successfully!'
        )

        return apiResponse
    }

    const logout = async (
        redirectPath = '/authentication/login.html?signout=1'
    ) => {
        // Prime CSRF (best-effort) then destroy the server-side session.
        await api
            .sentRequest('/sanctum/csrf-cookie', null, false, null, null, 'GET')
            .catch(() => null)
        await authApi.logout()

        clearItemsFromLocalStorage()

        // The RTK Query caches outlive this logout: nothing resets the store and the
        // router push is client-side, so the next user in this tab would be served the
        // previous user's responses for any query whose args carry no identity.
        reduxDispatch(laravel_api.util.resetApiState())
        reduxDispatch(legacyApiSlice.util.resetApiState())

        dispatch({ type: 'LOGOUT' })
        router.push(redirectPath)

        notificationContext.showInfo(
            'Goodbye!',
            'We hope to see you again soon!'
        )
    }

    const register = async (
        first_name,
        last_name,
        phone_number,
        email,
        password,
        company_name,
        company_url,
        token,
        reg_cookie,
        invite_ref_id,
        country_code,
        version_tag
    ) => {
        const apiResponse = await authApi.register({
            first_name,
            last_name,
            phone_number,
            email,
            password,
            company_name,
            company_url,
            token,
            reg_cookie,
            invite_ref_id,
            country_code,
            version_tag
        })

        const { errorCode, errorMessage } = apiResponse || {}
        const { user } = apiResponse.body || {}

        notificationContext.apiError(apiResponse, 'Account Registration')

        dispatch({
            type: 'REGISTER',
            payload: {
                user,
                errorMessage
            }
        })

        return { errorMessage, user }
    }

    const recover = async (usr_email) => {
        const apiResponse = await authApi.recover({ usr_email })

        const { errorCode, errorMessage } = apiResponse || {}
        const { recovery_email_sent, recovery_token } = apiResponse.body || {}

        notificationContext.apiError(apiResponse, 'Password Recovery')

        return { errorMessage, recovery_email_sent, recovery_token }
    }

    const recoverVerify = async (
        usr_email,
        recovery_token,
        recovery_code,
        password
    ) => {
        const apiResponse = await authApi.recoverVerify({
            usr_email,
            recovery_token,
            recovery_code,
            password
        })

        const { errorCode, errorMessage } = apiResponse || {}
        const { password_changed } = apiResponse.body || {}

        notificationContext.apiError(apiResponse, 'Password Reset')

        return { errorMessage, password_changed }
    }

    const registerVerify = async (usr_email, verification_code) => {
        const apiResponse = await authApi.registerVerify({
            usr_email,
            verification_code
        })

        const { errorCode, errorMessage } = apiResponse || {}
        const { account_verified, account_approved } = apiResponse.body || {}

        notificationContext.apiError(apiResponse, 'Account Verification')

        return { errorMessage, account_verified, account_approved }
    }

    const updatePassword = async (old_password, new_password) => {
        dispatch({
            type: 'UPDATE_PASSWORD_REQUEST',
            payload: {}
        })

        const apiResponse = await authApi.updatePassword(
            old_password,
            new_password
        )

        const { errorCode, errorMessage } = apiResponse || {}

        dispatch({
            type: 'UPDATE_PASSWORD_FINISHED',
            payload: {
                errorMessage
            }
        })

        notificationContext.apiError(apiResponse, 'Account Security')
        notificationContext.apiSuccess(
            apiResponse,
            'Account Security',
            'Your new password has been set.'
        )

        return apiResponse
    }

    const updateNotificationSetting = async (type, value) => {
        const apiResponse = await authApi.updateNotificationSetting(type, value)

        const { errorCode, errorMessage } = apiResponse || {}
        const {
            user,
            activeNetwork,
            userDefaultNetwork,
            userDefaultNetworkAcl,
            userNetworks,
            userRecentAccounts,
            segmentsCustom,
            userLogsAuth,
            organizationAccounts
        } = apiResponse.body || {}

        dispatch({
            type: 'UPDATE',
            payload: {
                user,
                activeNetwork,
                userDefaultNetwork,
                userDefaultNetworkAcl,
                segmentsCustom,
                userNetworks,
                userRecentAccounts,
                organizationAccounts,
                userLogsAuth,
                errorMessage
            }
        })

        notificationContext.apiError(apiResponse, 'Account Notifications')
        notificationContext.apiSuccess(
            apiResponse,
            'Account Notifications',
            'Nofication setting has been changed'
        )
    }

    const changeDefaultNetwork = async (network_id, forceRedirect = false) => {
        const apiResponse = await authApi.changeNetwork(network_id)

        const { errorCode, errorMessage } = apiResponse || {}
        const {
            user,
            activeNetwork,
            userDefaultNetwork,
            userDefaultNetworkAcl,
            userNetworks,
            userRecentAccounts,
            segmentsCustom,
            userLogsAuth,
            organizationAccounts
        } = apiResponse.body || {}

        const network = state.userNetworks.find(
            (userNetwork) => userNetwork.network_id === network_id
        )
        notificationContext.apiError(apiResponse, 'Change Account Network')
        // notificationContext.apiSuccess(apiResponse, "Change Account Network", `Your new network: ${network.network_short_code}`);

        if (forceRedirect) {
            dispatch({
                type: 'UPDATE',
                payload: {
                    user,
                    activeNetwork,
                    userDefaultNetwork,
                    userDefaultNetworkAcl,
                    segmentsCustom,
                    userNetworks,
                    userRecentAccounts,
                    organizationAccounts,
                    userLogsAuth,
                    errorMessage
                }
            })

            router.push('/')
            // setTimeout(() => {
            //   window.location.reload();
            // })
        } else {
            dispatch({
                type: 'UPDATE',
                payload: {
                    user,
                    activeNetwork,
                    userDefaultNetwork,
                    userDefaultNetworkAcl,
                    segmentsCustom,
                    userNetworks,
                    userRecentAccounts,
                    organizationAccounts,
                    userLogsAuth,
                    errorMessage
                }
            })
            router.push('/dashboard/')
        }
    }

    const createSegmentCustom = async (segment_name, segment_file) => {
        const apiResponse = await authApi.updateNotificationSetting(
            segment_name,
            segment_file
        )

        const { errorCode, errorMessage } = apiResponse || {}
        const {
            user,
            activeNetwork,
            userDefaultNetwork,
            userDefaultNetworkAcl,
            userNetworks,
            userRecentAccounts,
            segmentsCustom,
            userLogsAuth,
            organizationAccounts
        } = apiResponse.body || {}

        dispatch({
            type: 'UPDATE',
            payload: {
                user,
                activeNetwork,
                userDefaultNetwork,
                userDefaultNetworkAcl,
                segmentsCustom,
                userNetworks,
                userRecentAccounts,
                organizationAccounts,
                userLogsAuth,
                errorMessage
            }
        })

        notificationContext.apiError(apiResponse, 'Account Notifications')
        notificationContext.apiSuccess(
            apiResponse,
            'Account Notifications',
            'Nofication setting has been changed'
        )
    }

    const setAccountSwitchAsOpen = async () => {
        dispatch({
            type: 'OPEN_ACCOUNT_SWITCH',
            payload: {}
        })
    }
    const setAccountSwitchAsClose = async () => {
        dispatch({
            type: 'CLOSE_ACCOUNT_SWITCH',
            payload: {}
        })
    }

    const setOrganizationSwitchAsOpen = async () => {
        dispatch({
            type: 'OPEN_ORGANIZATION_SWITCH',
            payload: {}
        })
    }
    const setOrganizationSwitchAsClose = async () => {
        dispatch({
            type: 'CLOSE_ORGANIZATION_SWITCH',
            payload: {}
        })
    }

    const updateDataLayer = (newUser) => {
        if (!newUser) {
            return
        }

        if (!newUser.usr_first_name) {
            return
        }

        gtm.push({
            event: 'userinfo',
            event_action: 'update',
            first_name: newUser.usr_first_name,
            last_name: newUser.usr_last_name,
            email: newUser.usr_email,
            phone_number: newUser.usr_phone_number,
            company_name: newUser.usr_company_name,
            company_url: newUser.usr_company_url
        })
    }

    return (
        <AuthContext.Provider
            value={{
                ...state,
                platform: 'session',
                setAccountSwitchAsOpen,
                setAccountSwitchAsClose,

                setOrganizationSwitchAsOpen,
                setOrganizationSwitchAsClose,

                changeDefaultNetwork,
                login,
                me,
                tryToReLoginWithToken,
                update,
                logout,
                register,
                recover,
                recoverVerify,
                registerVerify,
                uploadAvatar,
                uploadLogo,
                removeLogo,
                updateNotificationSetting,
                updatePassword,
                changeMenuVisibility,
                updateDataLayer,
                aboutMe
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}

AuthProvider.propTypes = {
    children: PropTypes.node.isRequired
}

export const AuthConsumer = AuthContext.Consumer
